import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LegalAgreement, LegalDocuments, LegalDocumentType } from '@innovation-os-ng/core/models';
import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { firstValueFrom } from 'rxjs';
import { ApiResponse } from '../contracts/api-response';
import { LegalContractService } from '../contracts/legal-contract.service';

export class LegalDocumentException {
  constructor(protected exception?: ApiResponse) {}
}

export class ContractAlreadyExistsException extends LegalDocumentException {}

const LEGAL_DOCUMENT_ERRORS: { [errorCode: string]: typeof LegalDocumentException } = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ContractAlreadyExistsException
};

@Injectable()
export class ApiLegalContractService implements LegalContractService {
  private readonly restUrl: string;

  constructor(private readonly environmentService: EnvironmentService, private readonly httpClient: HttpClient) {
    this.restUrl = this.environmentService.getRestUrl();
  }

  public getContractDocuments(language: string): Promise<Array<LegalDocuments>> {
    return firstValueFrom(
      this.httpClient.get<Array<LegalDocuments>>(`${this.restUrl}/legal/contracts`, {
        params: {
          language
        }
      })
    );
  }

  public getContractsForTenant(tenantUri: string, language: string): Promise<Array<LegalDocuments>> {
    return firstValueFrom(
      this.httpClient.get<Array<LegalDocuments>>(`${this.restUrl}/legal/contracts/${tenantUri}`, {
        params: {
          language
        }
      })
    );
  }

  public async saveContractForTenant(tenantUri: string, contract: Partial<LegalDocuments>): Promise<Array<LegalDocuments>> {
    try {
      return await firstValueFrom(
        this.httpClient.put<Array<LegalDocuments>>(`${this.restUrl}/legal/contracts/${tenantUri}`, {
          language: contract.language,
          text: contract.text,
          type: contract.type,
          title: contract.title,
          state: contract.state
        })
      );
    } catch (error) {
      this.throwOnErrors(error);
    }
  }

  /**
   * Returns Pending agreements for tenant contract providing tenantUri
   */
  public getPendingAgreementsForTenantContracts(
    tenantUri: string,
    type?: LegalDocumentType,
    language?: string
  ): Promise<Array<LegalDocuments>> {
    const params: { type?: string; language?: string } = {};
    if (type) {
      params.type = type;
    }
    if (language) {
      params.language = language;
    }
    return firstValueFrom(
      this.httpClient.get<Array<LegalDocuments>>(`${this.restUrl}/legal/agreements/${tenantUri}/`, {
        params
      })
    );
  }

  /**
   * Returns Pending agreements for tenant contract providing tenantSlug
   */

  public getPendingAgreementsForTenantContractsByTenantSlug(
    tenantSlug: string,
    type?: LegalDocumentType,
    language?: string
  ): Promise<Array<LegalDocuments>> {
    const params: { type?: string; language?: string } = {};
    if (type) {
      params.type = type;
    }
    if (language) {
      params.language = language;
    }
    return firstValueFrom(
      this.httpClient.get<Array<LegalDocuments>>(`${this.restUrl}/legal/agreements/${tenantSlug}/slug`, {
        params
      })
    );
  }

  public async saveAnAgreementForTenant(tenantUri: string, contract: Partial<LegalDocuments>): Promise<Array<LegalAgreement>> {
    try {
      return await firstValueFrom(
        this.httpClient.post<Array<LegalAgreement>>(`${this.restUrl}/legal/agreements/${tenantUri}`, {
          language: contract.language,
          type: contract.type,
          version: contract.version
        })
      );
    } catch (error) {
      this.throwOnErrors(error);
    }
  }

  public async getPendingGlobalContracts(language: string, type?: string, userContractsOnly = false): Promise<Array<LegalDocuments>> {
    const params: { language: string; type?: string } = { language };
    if (type) {
      params.type = type;
    }
    const contracts = await firstValueFrom(
      this.httpClient.get<Array<LegalDocuments>>(`${this.restUrl}/legal/agreements`, {
        params
      })
    );

    if (!userContractsOnly) {
      return contracts;
    }

    // Filter out tenant-level contracts since they're not being processed
    // correctly and would confuse the user when showing the "We've updated our
    // Terms" modal.
    return contracts.filter((contract) => ['AVV', 'TERMS_AND_CONDITIONS'].indexOf(contract.type) === -1);
  }

  public saveAgreementToGlobalContract(language: string, version: string, type: string): Promise<LegalAgreement> {
    return firstValueFrom(
      this.httpClient.post<LegalAgreement>(`${this.restUrl}/legal/agreements`, {
        language,
        version,
        type
      })
    );
  }

  /**
   *
   * @throws ContractAlreadyExistsException
   *
   * @param response
   */
  private throwOnErrors(response: ApiResponse): void {
    if (!(response instanceof HttpErrorResponse)) {
      throw response;
    }
    if (!response.error.error) {
      throw response;
    }
    const apiException = LEGAL_DOCUMENT_ERRORS[response.error.error];
    if (!apiException) {
      throw new Error(response.error.error);
    }

    throw new apiException(response);
  }
}
